// @ts-nocheck
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Box,
  Button,
  Icon,
  Switch,
  FormControlLabel,
} from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../../common/components';
import { DateUtils, StringUtils } from '../../../../../common/utils';
import { useDialog, useOnChange } from '../../../../../common/hooks';
import PromotionCreateForm from '../../common/PromotionCreateForm';

const useStyles = makeStyles(() => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    maxWidth: '1200px',
  },
}));

function PromotionList(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const {
    data,
    products,
    onCreatePromotion,
    onCreatePromotionPeriod,
    onSelectPromotion,
    onCreatePromotionConfirm,
    onPageChange,
    page,
    actionsDisabled,
    filterParams,
    onFilterChange,
    sortParams,
    onSortChange,
    getTypeDisplayValue,
  } = props;

  const tutorialLink = <a target="_blank" href="https://tv2cms.atlassian.net/wiki/x/EQA0HwE">
    {t('promotion.promotionTutorial')}
  </a>

  const [sortModel, setSortModel] = useState([
    {
      field: sortParams.orderBy,
      sort: sortParams.sortOrder,
    },
  ]);

  useOnChange(sortModel, () => {
    if (_.isEmpty(sortModel)) {
      return;
    }

    onSortChange({
      orderBy: sortModel[0].field,
      sortOrder: sortModel[0].sort,
    });
  });

  const handleSortModelChange = params => {
    if (!_.isEqual(params.sortModel, sortModel)) {
      setSortModel(params.sortModel);
    }
  };

  const getColumnDefs = () => [
    {
      field: 'id',
      headerName: t('promotion.promotionId'),
      width: 135,
    },
    {
      field: 'code',
      headerName: t('promotion.promotionCode'),
      width: 250,
      flex: 1,
    },
    {
      field: 'type',
      headerName: t('promotion.promotionType'),
      width: 210,
      valueGetter: params => getTypeDisplayValue(params.value),
    },
    {
      field: 'created_at',
      headerName: t('promotion.promotionCreated'),
      width: 200,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.row.createdAt),
      sortable: false,
    },
    {
      field: 'discount_duration',
      headerName: t('promotion.promotionDiscountDuration'),
      width: 200,
      valueGetter: params => StringUtils.getFormattedDays(params.row.discountDuration),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'discount_type',
      headerName: t('promotion.promotionDiscountType'),
      width: 200,
      valueGetter: params => params.row.discountType,
    },
    {
      field: 'discount_value',
      headerName: t('promotion.promotionDiscountValue'),
      width: 175,
      valueGetter: params => params.row.discountType === 'PERCENT' ? `${params.row.discountValue} %` : StringUtils.getFormattedSum(params.row.discountValue),
    },
  ];

  const getRowData = () => data.content;

  const handleShowCreatePromotionDialog = () => {
    showDialog({
      title: t('promotion.promotionCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <PromotionCreateForm
            products={products}
            onCreatePromotion={onCreatePromotion}
            onCreatePromotionPeriod={onCreatePromotionPeriod}
            onFinish={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('common.create')}
            getTypeDisplayValue={getTypeDisplayValue}
          />
        </Box>
      ),
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
      link: tutorialLink,
    });
  };

  const handleFilterChange = event => {
    const filter = {
      ...filterParams,
    };

    if (event.target.id === 'PromotionList-showWithActivePeriods') {
      filter.withActivePeriods = event.target.checked;
    } else if (event.target.id === 'PromotionList-showActive') {
      filter.onlyActive = event.target.checked;
    }

    onFilterChange(filter);
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="PromotionList-showActive"
            color="primary"
            checked={filterParams.onlyActive}
            onChange={handleFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('common.onlyActive')}
      />
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="PromotionList-showWithActivePeriods"
            color="primary"
            checked={filterParams.withActivePeriods}
            onChange={handleFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('promotion.promotionShowWithActivePeriods')}
      />
      <Button
        id="PromotionList-createPromotion"
        variant="outlined"
        color="primary"
        startIcon={<Icon>discount</Icon>}
        onClick={handleShowCreatePromotionDialog}
        disabled={actionsDisabled}
      >
        {t('promotion.promotionCreate')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('common.empty')} />
    </GridOverlay>
  );

  return (
    <Box id="PromotionList-container" className={classes.container}>
      <DataTable
        id="PromotionList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        rowCount={data.total}
        onPageChange={onPageChange}
        onRowClick={onSelectPromotion}
        pageSize={20}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        sortingMode="server"
        paginationMode="server"
        pageIndex={page - 1}
        pagination
      />
    </Box>
  );
}

PromotionList.propTypes = {
  data: PropTypes.shape({
    size: PropTypes.number,
    items: PropTypes.number,
    total: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number,
    content: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  products: PropTypes.arrayOf(PropTypes.object),
  onCreatePromotion: PropTypes.func.isRequired,
  onSelectPromotion: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  filterParams: PropTypes.shape({
    onlyActive: PropTypes.bool,
    withActivePeriods: PropTypes.bool,
  }).isRequired,
  sortParams: PropTypes.shape({
    orderBy: PropTypes.string,
    sortOrder: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  getTypeDisplayValue: PropTypes.func.isRequired,
};

export default PromotionList;
